import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { initials } from '../lib/format';
import type { Employee, Role, Vendor } from '../lib/types';

export function PageHead({
  title,
  lede,
  actions,
}: {
  title: string;
  lede?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-head row-between wrap">
      <div>
        <h1>{title}</h1>
        {lede && <p className="lede">{lede}</p>}
      </div>
      {actions && <div className="row wrap">{actions}</div>}
    </header>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="loading">
      <span className="spinner" aria-hidden="true" />
      <span>{label}…</span>
    </div>
  );
}

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="alert" role="alert">
      {message}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children}
    </div>
  );
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint && <span className="xs subtle">{hint}</span>}
    </div>
  );
}

export function Avatar({ name }: { name: string | null }) {
  return (
    <span className="avatar" aria-hidden="true">
      {initials(name)}
    </span>
  );
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  size,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** 'xl' for a form with a lot to read side by side, such as a recommendation. */
  size?: 'xl';
}) {
  /* Escape closes, and the body does not scroll behind the dialog. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target !== e.currentTarget) return;
        /* A mousedown on the backdrop's OWN SCROLLBAR reports the backdrop as
           its target, so the plain target check that used to be here closed
           the dialog the moment somebody grabbed the bar to scroll a long
           form — taking everything they had typed with it. The scrollbar sits
           outside the element's client box, which is how it is told apart
           from a click on the scrim. */
        if (e.clientX > e.currentTarget.clientWidth) return;
        if (e.clientY > e.currentTarget.clientHeight) return;
        onClose();
      }}
    >
      <div className={`modal${size === 'xl' ? ' modal-xl' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/* Where the browser can size a field from its own content, CSS does the job
   and does it correctly by construction. The measuring below is the fallback
   for browsers that cannot, and must not run otherwise: an inline height would
   override field-sizing and reintroduce the very drift it exists to avoid. */
const SUPPORTS_FIELD_SIZING =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('field-sizing', 'content');

/**
 * A textarea that grows with its content and saves as you type.
 *
 * Used across the V/TO and charter, which are long-form documents where a
 * Save button per paragraph would be worse than useless.
 */
export function InlineEdit({
  value,
  onSave,
  placeholder,
  rows = 2,
  className,
  ...rest
}: {
  value: string | null;
  onSave: (next: string) => void;
  placeholder?: string;
  rows?: number;
} & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'>) {
  const [draft, setDraft] = useState(value ?? '');
  const ref = useRef<HTMLTextAreaElement>(null);
  const dirty = useRef(false);

  /* Accept upstream changes only while the user is not mid-edit, so a
     background reload cannot wipe what someone is typing. */
  useEffect(() => {
    if (!dirty.current) setDraft(value ?? '');
  }, [value]);

  /* scrollHeight is content + padding, but box-sizing is border-box, so the
     height we set has to carry the border too — otherwise every box ends up
     two pixels short and grows a scrollbar. */
  const resize = () => {
    if (SUPPORTS_FIELD_SIZING) return;
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  };
  useEffect(resize, [draft]);

  /* Text rewraps whenever the column changes width, so remeasure then too.
     This also rescues a box first measured while its container had no width
     yet — the height would otherwise stay wrong with nothing to scroll. */
  useEffect(() => {
    const el = ref.current;
    if (SUPPORTS_FIELD_SIZING || !el || typeof ResizeObserver === 'undefined') return;
    let lastWidth = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === lastWidth) return; // our own height change
      lastWidth = el.clientWidth;
      resize();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* The text faces arrive from Google after first paint (tokens.css asks for
     display=swap), and the swap rewraps text without touching either the draft
     or the column width — so neither effect above fires, and a box measured in
     the fallback face would stay a line short for good. */
  useEffect(() => {
    if (SUPPORTS_FIELD_SIZING || typeof document === 'undefined' || !document.fonts) return;
    let live = true;
    void document.fonts.ready.then(() => {
      if (live) resize();
    });
    return () => {
      live = false;
    };
  }, []);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    dirty.current = true;
    setDraft(e.target.value);
  };

  return (
    <textarea
      {...rest}
      ref={ref}
      className={className ?? 'inline-edit'}
      rows={rows}
      value={draft}
      placeholder={placeholder}
      onChange={handleChange}
      onBlur={() => {
        dirty.current = false;
        if ((value ?? '') !== draft) onSave(draft);
      }}
    />
  );
}

/** Employee dropdown. Used anywhere a record needs an owner. */
/**
 * A one-line text box that grows downward as it is typed into, instead of
 * scrolling sideways. For notes that sit in a row of form controls, where a
 * full textarea would be too tall for what is usually one line.
 *
 * Controlled, unlike InlineEdit: the caller owns the value and saves it. The
 * sizing is the same — CSS field-sizing where the browser has it, measuring
 * where it does not.
 */
export function GrowingTextarea({
  className,
  value,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (SUPPORTS_FIELD_SIZING) return;
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      className={`input textarea-grow${className ? ` ${className}` : ''}`}
      {...rest}
    />
  );
}

export function EmployeePicker({
  employees,
  value,
  onChange,
  placeholder = 'Unassigned',
}: {
  employees: Employee[];
  value: number | null;
  onChange: (id: number | null) => void;
  placeholder?: string;
}) {
  return (
    <select
      className="select"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
    >
      <option value="">{placeholder}</option>
      {employees.map((e) => (
        <option key={e.EmployeeId} value={e.EmployeeId}>
          {e.FullName}
          {e.RoleTitle ? ` — ${e.RoleTitle}` : ''}
        </option>
      ))}
    </select>
  );
}

/**
 * Pick a job role from the directory.
 *
 * Every screen that used to let somebody TYPE a job title now shows this. A
 * typed title was resolved against `core.Role` and a row created when nothing
 * matched, which made the People module a second place job titles are born —
 * one typo in a People Template and the shared directory every other app reads
 * grew a role called "Servce Desk Manager". Choosing from the list cannot do
 * that.
 *
 * The empty option's wording is the whole compensation for the freedom this
 * takes away: somebody who cannot find their role needs to know where roles
 * come from, not just that this one is missing.
 */
export function RolePicker({
  roles,
  value,
  onChange,
  placeholder = 'No role',
}: {
  roles: Role[];
  value: number | null;
  onChange: (id: number | null) => void;
  placeholder?: string;
}) {
  /* Inactive roles are kept when one is already selected — a retired title is
     still the title these questions were written for, and dropping it out of
     the list would silently reassign the row on the next save. */
  const shown = roles.filter((r) => r.IsActive || r.RoleId === value);

  return (
    <select
      className="select"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
    >
      <option value="">{placeholder}</option>
      {shown.map((r) => (
        <option key={r.RoleId} value={r.RoleId}>
          {r.Title}
          {r.IsActive ? '' : ' (inactive)'}
        </option>
      ))}
    </select>
  );
}

/**
 * Pick a vendor from the shared directory.
 *
 * A select, deliberately, and with no "add one" affordance: core.Vendor has a
 * single write path in directory.ts, and a box that could mint a row while
 * saving a product is exactly how the estate ended up with four spellings of
 * ConnectWise. A vendor that is genuinely new is added in Settings first.
 */
export function VendorPicker({
  vendors,
  value,
  onChange,
  placeholder = 'No vendor',
}: {
  vendors: Vendor[];
  value: number | null;
  onChange: (id: number | null) => void;
  placeholder?: string;
}) {
  /* An inactive vendor stays listed while it is the one selected — the product
     it is on was really bought from them, and dropping it out of the list
     would silently clear the field on the next save. */
  const shown = vendors.filter((v) => v.IsActive || v.VendorId === value);

  return (
    <select
      className="select"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
    >
      <option value="">{placeholder}</option>
      {shown.map((v) => (
        <option key={v.VendorId} value={v.VendorId}>
          {v.Name}
          {v.IsActive ? '' : ' (inactive)'}
        </option>
      ))}
    </select>
  );
}

/** 1-5 scoring buttons, used by the OML assessment and GWC confidence. */
export function ScaleInput({
  value,
  onChange,
  max = 5,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
  max?: number;
}) {
  return (
    <div className="scale" role="group">
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          type="button"
          className={value === n ? 'on' : ''}
          aria-pressed={value === n}
          /* Clicking the selected value clears it — otherwise a mis-click can
             never be undone back to "not scored". */
          onClick={() => onChange(value === n ? null : n)}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

export function Meter({ value, max = 5 }: { value: number | null; max?: number }) {
  const pct = value === null ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="meter" aria-hidden="true">
      <span className={value !== null && value / max < 0.5 ? 'low' : ''} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Delete button that asks first. Every destructive action in the app uses it. */
/**
 * A destructive action behind a confirm.
 *
 * `consequence` replaces the default warning for the callers where it is not
 * true. Most deletes here cannot be undone; some are a flag flip with a way
 * back, and telling somebody an action is permanent when it is not makes them
 * hesitate over the reversible ones and trust the warning less on the real
 * ones.
 */
export function DeleteButton({
  onConfirm,
  label = 'Delete',
  what,
  verb = 'Delete',
  consequence = 'This cannot be undone.',
}: {
  onConfirm: () => void;
  label?: string;
  what: string;
  /** The word in the prompt, when the button is not called Delete. */
  verb?: string;
  consequence?: string;
}) {
  return (
    <button
      type="button"
      className="btn btn-danger btn-sm"
      onClick={() => {
        if (window.confirm(`${verb} ${what}? ${consequence}`)) onConfirm();
      }}
    >
      {label}
    </button>
  );
}
